import type { ComponentType } from 'react';

import type { StrictRJSFSchema } from '@rjsf/utils';

import type { FormWriterContext } from '@/components/forms/components/form/writer';
import type { WidgetWriterProps } from '@/components/forms/components/form/writer/widgets/widget.type';

/**
 * HOC to bring inline error validation to our widgets.
 * @function withInlineValidation
 * @param WrappedComponent - widget
 * @returns - wrapped component with onBlur event and errorMessage
 */
export const withInlineValidation =
  <
    T,
    S extends StrictRJSFSchema,
    F extends FormWriterContext,
    P extends WidgetWriterProps<T, S, F>
  >(
    WrappedComponent: ComponentType<P>
  ) =>
  (props: P) => {
    const {
      label,
      schema: { title },
      formContext,
      rawErrors
    } = props;

    const { blurredFields, onErrorTriggered, onBlur } = formContext!;

    const handleBlur = () =>
      label &&
      setTimeout(() => onBlur(label.replace(' ', '_').toLowerCase()), 0);

    // This sets the border to red and this cannot be done through the formTemplates for the field.
    const invalid =
      rawErrors &&
      rawErrors.length > 0 &&
      ((label &&
        blurredFields.includes(label.toLowerCase().replace(' ', '_'))) ||
        onErrorTriggered);

    return (
      <>
        <div onBlur={handleBlur}>
          <WrappedComponent invalid={invalid} {...props} />
        </div>
        {invalid && (
          <div
            className="mt-1 text-destructive text-xs"
            data-cy={`error-message-${props.name}`}
            id="field-error-message"
          >
            The <i className="font-semibold italic">{label || title}</i> field{' '}
            <span>{rawErrors.join(', ')}.</span>
          </div>
        )}
      </>
    );
  };
